// SalePopup.js
import {
  defineComponent,
  ref,
  toRefs,
  watch,
  onMounted,
} from "/Scripts/vue.esm-browser.js";

const html = String.raw;

export default defineComponent({
  name: "SalePopup",
  props: {
    shopCode: {
      type: String,
    },
  },
  emits: [],
  setup(props, { emit }) {
    const popupContent = ref("");
    const isLoading = ref(false);
    const hasError = ref(false);
    const { shopCode } = toRefs(props);

    // 檢查是否為首頁內容（被重定向）
    const isHomepageContent = (response, requestedUrl, content = null) => {
      // 方法1: 檢查URL是否被重定向
      if (!response.url.includes(requestedUrl)) {
        return true;
      }

      // 方法2: 檢查內容中是否有大量meta標籤（首頁特徵）
      if (content) {
        const metaCount = (content.match(/<meta[^>]*>/gi) || []).length;
        // 如果有超過2個meta標籤，很可能是首頁
        if (metaCount > 2) {
          console.log(`檢測到 ${metaCount} 個meta標籤，判斷為首頁內容`);
          return true;
        }
      }

      return false;
    };

    // 載入並執行JS檔案
    const loadPopupScript = async (shopCode) => {
      const timestamp = new Date().getTime();
      const scriptUrl = `/Content/Shop/${shopCode}/popup/popup.js?t=${timestamp}`;

      try {
        const response = await fetch(scriptUrl);

        // 檢查是否成功且未被重定向到首頁
        if (response.ok) {
          const scriptContent = await response.text();

          if (!isHomepageContent(response, scriptUrl, scriptContent)) {
            // 創建並執行script標籤
            const script = document.createElement("script");
            script.textContent = scriptContent;
            script.setAttribute("data-popup-script", shopCode); // 標記此script以便後續管理
            document.head.appendChild(script);

            console.log("成功載入popup JS檔案:", scriptUrl);
            return true;
          } else {
            console.log("popup JS檔案被重定向到首頁:", scriptUrl);
            return false;
          }
        } else {
          console.log("popup JS檔案不存在:", scriptUrl);
          return false;
        }
      } catch (error) {
        console.log("載入popup JS檔案時發生錯誤:", error);
        return false;
      }
    };

    // 移除之前載入的popup script
    const removePreviousScript = (shopCode) => {
      const existingScript = document.querySelector(
        `script[data-popup-script="${shopCode}"]`,
      );
      if (existingScript) {
        existingScript.remove();
      }
    };

    // 執行HTML中的inline scripts
    const executeInlineScripts = (htmlContent) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, "text/html");
      const scripts = doc.querySelectorAll("script");

      scripts.forEach((script) => {
        try {
          // 創建新的script元素並執行
          const newScript = document.createElement("script");
          newScript.textContent = script.textContent;

          // 如果有src屬性，保留它
          if (script.src) {
            newScript.src = script.src;
          }

          // 如果有其他屬性，也複製過去
          Array.from(script.attributes).forEach((attr) => {
            if (attr.name !== "src") {
              newScript.setAttribute(attr.name, attr.value);
            }
          });

          // 添加到文檔中執行
          document.head.appendChild(newScript);

          // 立即移除以避免重複執行
          setTimeout(() => {
            newScript.remove();
          }, 0);

          console.log("成功執行inline script");
        } catch (error) {
          console.error("執行inline script時發生錯誤:", error);
        }
        // 從原始HTML中移除script標籤
        script.remove();
      });

      // 返回處理後的HTML（已移除script標籤）
      return doc.documentElement.outerHTML;
    };

    const loadPopupContent = async () => {
      if (!props.shopCode) {
        console.warn("shopCode 未提供");
        return;
      }

      const timestamp = new Date().getTime();
      const url = `/Content/Shop/${props.shopCode}/popup/popup.html?t=${timestamp}`;
      isLoading.value = true;
      hasError.value = false;

      try {
        const response = await fetch(url);

        if (response.ok) {
          const content = await response.text();

          if (!isHomepageContent(response, url, content)) {
            // 先執行HTML中的inline scripts，然後設置處理後的內容
            const processedContent = executeInlineScripts(content);
            popupContent.value = processedContent;
            console.log("成功載入popup HTML內容並執行inline scripts");

            // 載入HTML成功後，嘗試載入對應的JS檔案
            removePreviousScript(props.shopCode); // 先移除之前的script
            await loadPopupScript(props.shopCode);
          } else {
            console.log("popup HTML檔案被重定向到首頁:", url);
            popupContent.value = "";
          }
        } else {
          console.log("popup HTML檔案不存在:", url);
          popupContent.value = "";
        }
      } catch (error) {
        console.error("載入popup檔案時發生錯誤:", error);
        hasError.value = true;
        popupContent.value = "";
      } finally {
        isLoading.value = false;
      }
    };

    onMounted(() => {
      loadPopupContent();
    });

    // 監聽shopCode變化，重新載入內容
    watch(
      () => shopCode.value,
      () => {
        loadPopupContent();
      },
    );

    return {
      popupContent,
      isLoading,
      hasError,
      loadPopupContent,
    };
  },
  template: html`
    <div class="sale-popup">
      <div
        v-if="popupContent"
        v-html="popupContent"
        class="popup-content"
      ></div>
    </div>
  `,
  methods: {},
});
